import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import {
    X,
    Download,
    Image as ImageIcon,
    Bed,
    Bath,
    Square,
    MapPin,
    Phone,
    Mail,
    Sparkles,
    Check,
    ChevronLeft,
    ChevronRight
} from "lucide-react";
import {
    DEFAULT_PROPERTY_FLYER_TEMPLATE,
    PROPERTY_FLYER_TEMPLATES
} from "../config/propertyFlyerTemplates";

/**
 * Decode over-encoded S3 URLs (e.g., %252540 → %2540 → %40 → @)
 */
const decodeS3Url = (url) => {
    if (!url || typeof url !== 'string') return url;
    let decoded = url;
    let prev = '';
    while (decoded !== prev && decoded.includes('%25')) {
        prev = decoded;
        try {
            decoded = decodeURIComponent(decoded);
        } catch (e) {
            break;
        }
    }
    return decoded;
};

const FLYER_THEMES = {
    "light-luxury": {
        shell: "from-stone-50 via-white to-slate-100",
        panel: "bg-white/82",
        panelAlt: "bg-stone-100/75",
        textPrimary: "text-slate-900",
        textSecondary: "text-slate-600",
        textMuted: "text-slate-400",
        border: "border-white/80",
        badgeText: "text-white",
        headingFont: "\"Iowan Old Style\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "noir-editorial": {
        shell: "from-zinc-950 via-slate-900 to-stone-900",
        panel: "bg-white/8",
        panelAlt: "bg-white/10",
        textPrimary: "text-white",
        textSecondary: "text-white/75",
        textMuted: "text-white/45",
        border: "border-white/10",
        badgeText: "text-white",
        headingFont: "\"Didot\", \"Bodoni 72\", Georgia, serif",
        bodyFont: "\"Helvetica Neue\", \"Segoe UI\", sans-serif"
    },
    "sunlit-open-house": {
        shell: "from-orange-50 via-amber-50 to-rose-50",
        panel: "bg-white/86",
        panelAlt: "bg-orange-100/60",
        textPrimary: "text-stone-900",
        textSecondary: "text-stone-700",
        textMuted: "text-stone-500",
        border: "border-orange-100",
        badgeText: "text-white",
        headingFont: "\"Cormorant Garamond\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "midnight-glow": {
        shell: "from-indigo-950 via-slate-950 to-black",
        panel: "bg-white/8",
        panelAlt: "bg-indigo-500/10",
        textPrimary: "text-white",
        textSecondary: "text-white/70",
        textMuted: "text-white/45",
        border: "border-white/10",
        badgeText: "text-white",
        headingFont: "\"Didot\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "soft-minimal": {
        shell: "from-stone-100 via-white to-stone-200",
        panel: "bg-white/88",
        panelAlt: "bg-stone-100/70",
        textPrimary: "text-stone-900",
        textSecondary: "text-stone-600",
        textMuted: "text-stone-400",
        border: "border-stone-200",
        badgeText: "text-white",
        headingFont: "\"Helvetica Neue\", \"Segoe UI\", sans-serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "platinum-noir": {
        shell: "from-black via-zinc-950 to-slate-950",
        panel: "bg-white/6",
        panelAlt: "bg-white/8",
        textPrimary: "text-white",
        textSecondary: "text-white/72",
        textMuted: "text-white/45",
        border: "border-white/10",
        badgeText: "text-black",
        headingFont: "\"Didot\", Georgia, serif",
        bodyFont: "\"Helvetica Neue\", \"Segoe UI\", sans-serif"
    },
    "architectural-modern": {
        shell: "from-slate-100 via-white to-slate-200",
        panel: "bg-white/84",
        panelAlt: "bg-slate-100/72",
        textPrimary: "text-slate-950",
        textSecondary: "text-slate-700",
        textMuted: "text-slate-500",
        border: "border-slate-200",
        badgeText: "text-white",
        headingFont: "\"Helvetica Neue\", \"Arial Narrow\", sans-serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "warm-family": {
        shell: "from-amber-50 via-orange-50 to-stone-100",
        panel: "bg-white/88",
        panelAlt: "bg-amber-100/70",
        textPrimary: "text-stone-900",
        textSecondary: "text-stone-700",
        textMuted: "text-stone-500",
        border: "border-amber-100",
        badgeText: "text-white",
        headingFont: "\"Cormorant Garamond\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "signal-red": {
        shell: "from-rose-50 via-white to-orange-50",
        panel: "bg-white/88",
        panelAlt: "bg-rose-100/65",
        textPrimary: "text-slate-950",
        textSecondary: "text-slate-700",
        textMuted: "text-slate-500",
        border: "border-rose-100",
        badgeText: "text-white",
        headingFont: "\"Helvetica Neue\", sans-serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "broker-blue": {
        shell: "from-sky-50 via-white to-blue-100",
        panel: "bg-white/88",
        panelAlt: "bg-blue-100/70",
        textPrimary: "text-slate-950",
        textSecondary: "text-slate-700",
        textMuted: "text-slate-500",
        border: "border-blue-100",
        badgeText: "text-white",
        headingFont: "\"Helvetica Neue\", sans-serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "coastal-breeze": {
        shell: "from-cyan-50 via-sky-50 to-blue-100",
        panel: "bg-white/86",
        panelAlt: "bg-sky-100/72",
        textPrimary: "text-slate-900",
        textSecondary: "text-slate-600",
        textMuted: "text-slate-400",
        border: "border-sky-100",
        badgeText: "text-white",
        headingFont: "\"Fraunces\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "city-night": {
        shell: "from-zinc-900 via-slate-900 to-indigo-950",
        panel: "bg-white/8",
        panelAlt: "bg-white/10",
        textPrimary: "text-white",
        textSecondary: "text-white/72",
        textMuted: "text-white/45",
        border: "border-white/10",
        badgeText: "text-white",
        headingFont: "\"Bebas Neue\", \"Arial Narrow\", sans-serif",
        bodyFont: "\"Helvetica Neue\", \"Segoe UI\", sans-serif"
    },
    "stone-minimal": {
        shell: "from-stone-50 via-stone-100 to-neutral-200",
        panel: "bg-white/84",
        panelAlt: "bg-stone-200/65",
        textPrimary: "text-stone-900",
        textSecondary: "text-stone-700",
        textMuted: "text-stone-500",
        border: "border-stone-200",
        badgeText: "text-white",
        headingFont: "\"Helvetica Neue\", sans-serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "capital-green": {
        shell: "from-emerald-50 via-white to-lime-100",
        panel: "bg-white/86",
        panelAlt: "bg-emerald-100/68",
        textPrimary: "text-slate-950",
        textSecondary: "text-slate-700",
        textMuted: "text-slate-500",
        border: "border-emerald-100",
        badgeText: "text-white",
        headingFont: "\"Helvetica Neue\", sans-serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "heritage-warmth": {
        shell: "from-amber-50 via-orange-50 to-red-50",
        panel: "bg-white/88",
        panelAlt: "bg-amber-100/68",
        textPrimary: "text-stone-900",
        textSecondary: "text-stone-700",
        textMuted: "text-stone-500",
        border: "border-amber-100",
        badgeText: "text-white",
        headingFont: "\"Cormorant Garamond\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "earth-retreat": {
        shell: "from-lime-50 via-stone-100 to-emerald-100",
        panel: "bg-white/86",
        panelAlt: "bg-lime-100/68",
        textPrimary: "text-stone-900",
        textSecondary: "text-stone-700",
        textMuted: "text-stone-500",
        border: "border-lime-100",
        badgeText: "text-white",
        headingFont: "\"Fraunces\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "weekend-sunrise": {
        shell: "from-pink-50 via-rose-50 to-orange-100",
        panel: "bg-white/86",
        panelAlt: "bg-rose-100/68",
        textPrimary: "text-stone-900",
        textSecondary: "text-stone-700",
        textMuted: "text-stone-500",
        border: "border-rose-100",
        badgeText: "text-white",
        headingFont: "\"Didot\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "penthouse-noir": {
        shell: "from-black via-zinc-950 to-slate-950",
        panel: "bg-white/6",
        panelAlt: "bg-white/8",
        textPrimary: "text-white",
        textSecondary: "text-white/74",
        textMuted: "text-white/45",
        border: "border-white/10",
        badgeText: "text-black",
        headingFont: "\"Didot\", Georgia, serif",
        bodyFont: "\"Helvetica Neue\", \"Segoe UI\", sans-serif"
    },
    "lifestyle-rose": {
        shell: "from-rose-50 via-white to-pink-100",
        panel: "bg-white/88",
        panelAlt: "bg-rose-100/68",
        textPrimary: "text-slate-950",
        textSecondary: "text-slate-700",
        textMuted: "text-slate-500",
        border: "border-rose-100",
        badgeText: "text-white",
        headingFont: "\"Cormorant Garamond\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    },
    "executive-cream": {
        shell: "from-stone-50 via-amber-50 to-stone-100",
        panel: "bg-white/88",
        panelAlt: "bg-stone-100/72",
        textPrimary: "text-stone-950",
        textSecondary: "text-stone-700",
        textMuted: "text-stone-500",
        border: "border-stone-200",
        badgeText: "text-white",
        headingFont: "\"Iowan Old Style\", Georgia, serif",
        bodyFont: "\"Avenir Next\", \"Segoe UI\", sans-serif"
    }
};

const getPhotoUrl = (photo) => {
    return photo?.processedUrl || photo?.processed_url || photo?.stagedUrl || photo?.staged_url || photo?.originalUrl || photo?.original_url || photo?.url || null;
};

const formatDisplayPrice = (value) => {
    if (!value) {
        return "";
    }

    const trimmed = String(value).trim();
    return trimmed.startsWith("$") ? trimmed : `$${trimmed}`;
};

const formatDisplaySquareFeet = (value) => {
    if (!value) {
        return "";
    }

    const trimmed = String(value).trim().toLowerCase();
    if (trimmed.includes("sq")) {
        return value;
    }

    return `${value} sq ft`;
};

const loadImageAsDataUrl = (url) => new Promise((resolve) => {
    if (!url) {
        resolve(null);
        return;
    }

    // Skip if already a data URL
    if (url.startsWith('data:')) {
        resolve(url);
        return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
        try {
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth || image.width;
            canvas.height = image.naturalHeight || image.height;
            const context = canvas.getContext("2d");
            context.drawImage(image, 0, 0);
            // Use PNG for lossless quality preservation (no compression artifacts)
            resolve(canvas.toDataURL("image/png"));
        } catch (error) {
            console.warn("[Flyer] Failed to convert image to data URL:", error.message);
            // Return null to indicate failure - don't fall back to URL which will fail in canvas
            resolve(null);
        }
    };
    image.onerror = (e) => {
        console.warn("[Flyer] Failed to load image for conversion:", e);
        resolve(null);
    };
    // Add cache-busting to ensure fresh CORS request (avoid cached non-CORS response)
    const cacheBustUrl = url.includes('?') ? `${url}&_cors=${Date.now()}` : `${url}?_cors=${Date.now()}`;
    image.src = cacheBustUrl;
});

const PropertyFlyerBuilderModal = ({ isOpen, onClose, property, initialBranding }) => {
    const flyerRef = useRef(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState(DEFAULT_PROPERTY_FLYER_TEMPLATE);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isResolvingImages, setIsResolvingImages] = useState(false);
    const [flyerPhotos, setFlyerPhotos] = useState([]);
    const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState({});
    const [resolvedAgentPhoto, setResolvedAgentPhoto] = useState(null);
    const [resolvedBrokerageLogo, setResolvedBrokerageLogo] = useState(null);
    const [formData, setFormData] = useState({
        propertyName: "",
        propertyAddress: "",
        listingPrice: "",
        bedrooms: "",
        bathrooms: "",
        squareFeet: "",
        propertyDescription: "",
        agentName: "",
        agentTitle: "Real Estate Advisor",
        agentPhone: "",
        agentEmail: "",
        agentPhoto: null,
        brokerageName: "",
        brokerageLogo: null,
        customHeadline: "",
        accentColor: ""
    });

    useEffect(() => {
        if (!property) {
            return;
        }

        // Check nested 'property' object (how API stores property info)
        const propInfo = property.property || property.propertyInfo || {};

        // Get property name/address from multiple possible locations
        let propName = property.propertyName || property.property_name || property.name || property.title ||
                      propInfo.name || propInfo.propertyName || "";
        let propAddress = property.propertyAddress || property.property_address || property.address ||
                         propInfo.address || propInfo.propertyAddress || "";

        // If address is empty but name looks like an address, use it for both
        if (!propAddress && propName && /\d+.*,/.test(propName)) {
            propAddress = propName;
        }
        if (!propName && propAddress) {
            propName = propAddress;
        }

        // Get price - check initialBranding first, then property, then nested propInfo
        let price = initialBranding?.listingPrice ||
                   property.listingPrice || property.listing_price || property.price ||
                   propInfo.price || propInfo.listingPrice || "";

        // Get beds/baths/sqft
        let beds = initialBranding?.bedrooms ||
                  property.bedrooms || property.beds ||
                  propInfo.bedrooms || propInfo.beds || "";
        let baths = initialBranding?.bathrooms ||
                   property.bathrooms || property.baths ||
                   propInfo.bathrooms || propInfo.baths || "";
        let sqft = initialBranding?.squareFeet ||
                  property.squareFeet || property.square_feet || property.sqft ||
                  propInfo.squareFeet || propInfo.sqft || "";

        // Get description
        let description = initialBranding?.propertyDescription ||
                         property.propertyDescription || property.property_description || property.description ||
                         propInfo.description || propInfo.propertyDescription || "";

        // Format price and sqft with commas if numeric
        const formattedPrice = typeof price === 'number' ? price.toLocaleString() : (price || "");
        const formattedSqft = typeof sqft === 'number' ? sqft.toLocaleString() : (sqft || "");

        // Decode S3 URLs for agent photo and brokerage logo
        const decodedAgentPhoto = decodeS3Url(initialBranding?.agentPhoto) || null;
        const decodedBrokerageLogo = decodeS3Url(initialBranding?.brokerageLogo) || null;

        console.log("[FlyerBuilder] Property data:", {
            raw: property,
            propInfo,
            initialBranding,
            mapped: { propName, propAddress, price: formattedPrice, beds, baths, sqft: formattedSqft, description }
        });

        setFormData({
            propertyName: propName,
            propertyAddress: propAddress,
            listingPrice: formattedPrice,
            bedrooms: String(beds || ""),
            bathrooms: String(baths || ""),
            squareFeet: formattedSqft,
            propertyDescription: description,
            agentName: initialBranding?.agentName || "",
            agentTitle: initialBranding?.agentTitle || "Real Estate Advisor",
            agentPhone: initialBranding?.agentPhone || "",
            agentEmail: initialBranding?.agentEmail || "",
            agentPhoto: decodedAgentPhoto,
            brokerageName: initialBranding?.brokerageName || "",
            brokerageLogo: decodedBrokerageLogo,
            customHeadline: "",
            accentColor: initialBranding?.accentColor || ""
        });
    }, [property, initialBranding]);

    const photos = useMemo(() => {
        return (property?.photos || [])
            .map((photo, index) => ({
                id: photo.photoId || photo.photo_id || photo.id || index,
                room: photo.roomType || photo.room || "Room",
                url: getPhotoUrl(photo)
            }))
            .filter((photo) => photo.url);
    }, [property]);

    useEffect(() => {
        setFlyerPhotos(photos);
    }, [photos]);

    useEffect(() => {
        let isCancelled = false;

        const resolvePhotos = async () => {
            if (!flyerPhotos.length) {
                setResolvedPhotoUrls({});
                return;
            }

            setIsResolvingImages(true);

            const resolvedEntries = await Promise.all(
                flyerPhotos.map(async (photo) => {
                    const resolvedUrl = await loadImageAsDataUrl(photo.url);
                    return [photo.id, resolvedUrl || photo.url];
                })
            );

            if (!isCancelled) {
                setResolvedPhotoUrls(Object.fromEntries(resolvedEntries));
                setIsResolvingImages(false);
            }
        };

        resolvePhotos();

        return () => {
            isCancelled = true;
        };
    }, [flyerPhotos]);

    // Resolve agent photo and brokerage logo to data URLs for PDF export
    useEffect(() => {
        const resolveAgentAssets = async () => {
            if (formData.agentPhoto) {
                const resolved = await loadImageAsDataUrl(formData.agentPhoto);
                setResolvedAgentPhoto(resolved || formData.agentPhoto);
            } else {
                setResolvedAgentPhoto(null);
            }

            if (formData.brokerageLogo) {
                const resolved = await loadImageAsDataUrl(formData.brokerageLogo);
                setResolvedBrokerageLogo(resolved || formData.brokerageLogo);
            } else {
                setResolvedBrokerageLogo(null);
            }
        };

        resolveAgentAssets();
    }, [formData.agentPhoto, formData.brokerageLogo]);

    const selectedTemplate = PROPERTY_FLYER_TEMPLATES.find((template) => template.id === selectedTemplateId) || PROPERTY_FLYER_TEMPLATES[0];
    const theme = FLYER_THEMES[selectedTemplate.theme] || FLYER_THEMES["light-luxury"];
    const accentColor = formData.accentColor || selectedTemplate.accentColor;
    const headline = formData.customHeadline || formData.propertyName || "Signature Property";
    const stats = [
        formData.bedrooms ? { icon: Bed, label: "Beds", value: formData.bedrooms } : null,
        formData.bathrooms ? { icon: Bath, label: "Baths", value: formData.bathrooms } : null,
        formData.squareFeet ? { icon: Square, label: "Sq Ft", value: formatDisplaySquareFeet(formData.squareFeet) } : null
    ].filter(Boolean);
    const featuredRooms = [...new Set(flyerPhotos.map((photo) => photo.room).filter(Boolean))].slice(0, 4);

    const getResolvedPhotoUrl = (photo) => {
        if (!photo) {
            return null;
        }
        return resolvedPhotoUrls[photo.id] || photo.url;
    };

    const heroPhoto = getResolvedPhotoUrl(flyerPhotos[0]);
    const secondaryPhoto = getResolvedPhotoUrl(flyerPhotos[1]) || heroPhoto;
    const tertiaryPhoto = getResolvedPhotoUrl(flyerPhotos[2]) || secondaryPhoto;
    const quaternaryPhoto = getResolvedPhotoUrl(flyerPhotos[3]) || tertiaryPhoto;

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const moveFlyerPhoto = (fromIndex, toIndex) => {
        setFlyerPhotos((prev) => {
            if (
                fromIndex < 0 ||
                toIndex < 0 ||
                fromIndex >= prev.length ||
                toIndex >= prev.length
            ) {
                return prev;
            }

            const next = [...prev];
            const [movedPhoto] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, movedPhoto);
            return next;
        });
    };

    const setHeroFlyerPhoto = (photoId) => {
        setFlyerPhotos((prev) => {
            const targetIndex = prev.findIndex((photo) => photo.id === photoId);
            if (targetIndex <= 0) {
                return prev;
            }

            const next = [...prev];
            const [targetPhoto] = next.splice(targetIndex, 1);
            next.unshift(targetPhoto);
            return next;
        });
    };

    const handleDownloadPDF = async () => {
        if (!flyerRef.current || isResolvingImages) {
            return;
        }

        setIsDownloading(true);
        try {
            const element = flyerRef.current;
            const filename = `${(headline || "property-flyer").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;

            // Get actual element dimensions to preserve exact aspect ratio
            const rect = element.getBoundingClientRect();
            const aspectRatio = rect.width / rect.height;

            // Use 11 inches as base width (standard letter landscape)
            const pdfWidth = 11;
            const pdfHeight = pdfWidth / aspectRatio;

            const options = {
                margin: 0,
                filename,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    allowTaint: false,
                    logging: false,
                    letterRendering: true,
                    foreignObjectRendering: false,
                    imageTimeout: 30000,
                    removeContainer: true,
                    backgroundColor: "#ffffff",
                    // Match exact preview dimensions
                    width: rect.width,
                    height: rect.height,
                    windowWidth: rect.width,
                    windowHeight: rect.height
                },
                jsPDF: { unit: "in", format: [pdfWidth, pdfHeight], orientation: "landscape" }
            };

            await html2pdf().set(options).from(element).save();
        } catch (error) {
            console.error("PDF export failed:", error);
            alert("PDF export failed. Please try downloading as HD Image instead.");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownloadImage = async () => {
        if (!flyerRef.current || isResolvingImages) {
            return;
        }

        setIsDownloading(true);
        try {
            const element = flyerRef.current;

            // Get actual element dimensions to preserve exact aspect ratio
            const rect = element.getBoundingClientRect();
            const aspectRatio = rect.width / rect.height;

            // Target width for high-res output (4K quality)
            const targetWidth = 3300;
            const targetHeight = Math.round(targetWidth / aspectRatio);

            // Clone the element to avoid modifying the original
            const clone = element.cloneNode(true);
            clone.style.position = "absolute";
            clone.style.left = "-9999px";
            clone.style.top = "0";
            // Set exact dimensions matching preview aspect ratio
            clone.style.width = `${rect.width}px`;
            clone.style.height = `${rect.height}px`;
            document.body.appendChild(clone);

            // Wait for images to load in the clone
            const images = clone.querySelectorAll("img");
            await Promise.all(
                Array.from(images).map(
                    (img) =>
                        new Promise((resolve) => {
                            if (img.complete) {
                                resolve();
                            } else {
                                img.onload = resolve;
                                img.onerror = resolve;
                            }
                        })
                )
            );

            // Calculate scale to achieve target resolution while preserving aspect ratio
            const scale = targetWidth / rect.width;

            const canvas = await html2canvas(clone, {
                scale: scale,
                useCORS: true,
                allowTaint: false,
                logging: false,
                backgroundColor: "#ffffff",
                imageTimeout: 30000,
                width: rect.width,
                height: rect.height,
                windowWidth: rect.width,
                windowHeight: rect.height
            });

            document.body.removeChild(clone);

            // Download as high-quality PNG (lossless)
            const link = document.createElement("a");
            link.download = `${(headline || "property-flyer").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-hd.png`;
            link.href = canvas.toDataURL("image/png", 1.0);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Image export failed:", error);
            alert("Image export failed. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    const renderPhoto = (url, className, roomLabel) => (
        <div className={`relative overflow-hidden bg-slate-100 ${className}`}>
            {url ? (
                <>
                    <img src={url} alt="" crossOrigin="anonymous" className="h-full w-full object-cover bg-black/[0.04]" />
                    {roomLabel && (
                        <div className="absolute bottom-3 left-3 rounded-full bg-black/55 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm">
                            {roomLabel}
                        </div>
                    )}
                </>
            ) : (
                <div className="flex h-full w-full items-center justify-center bg-slate-200 text-slate-500">
                    Add staged photos
                </div>
            )}
        </div>
    );

    const FlyerInfoPanel = ({ compact = false }) => (
        <div className={`rounded-[22px] border ${compact ? "p-4" : "p-6"} ${theme.panel} ${theme.border}`}>
            <div className="mb-3 inline-flex rounded-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.28em]" style={{ backgroundColor: accentColor, color: theme.badgeText === "text-black" ? "#0f172a" : "#ffffff" }}>
                {selectedTemplate.badge}
            </div>
            <h1 className={`${compact ? "text-[1.45rem]" : "text-[2rem]"} leading-[0.94] ${theme.textPrimary}`} style={{ fontFamily: theme.headingFont }}>
                {headline}
            </h1>
            <div className={`mt-2 flex items-start gap-2 ${compact ? "text-xs" : "text-sm"} ${theme.textSecondary}`}>
                <MapPin className={`${compact ? "mt-0.5 h-3 w-3" : "mt-0.5 h-4 w-4"}`} />
                <span>{formData.propertyAddress || "Property address"}</span>
            </div>
            {formData.listingPrice && (
                <div className={`mt-3 ${compact ? "text-xl" : "text-3xl"} font-semibold`} style={{ color: accentColor, fontFamily: theme.headingFont }}>
                    {formatDisplayPrice(formData.listingPrice)}
                </div>
            )}
            {stats.length > 0 && (
                <div className={`mt-4 grid ${compact ? "grid-cols-3 gap-2" : "grid-cols-3 gap-3"}`}>
                    {stats.map((stat) => {
                        const Icon = stat.icon;
                        return (
                            <div key={stat.label} className={`rounded-2xl border ${compact ? "p-2.5" : "p-3"} ${theme.panelAlt} ${theme.border}`}>
                                <div className={`flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] ${theme.textMuted}`}>
                                    <Icon className="h-3 w-3" />
                                    {stat.label}
                                </div>
                                <div className={`mt-1 ${compact ? "text-sm" : "text-base"} font-semibold ${theme.textPrimary}`}>
                                    {stat.value}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {featuredRooms.length > 0 && (
                <div className="mt-4">
                    <div className={`text-[9px] uppercase tracking-[0.18em] ${theme.textMuted}`}>Featured Rooms</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {featuredRooms.map((room) => (
                            <span
                                key={room}
                                className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${theme.panelAlt} ${theme.border} ${theme.textSecondary}`}
                            >
                                {room}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            <p className={`mt-4 ${compact ? "text-xs leading-5" : "text-sm leading-6"} ${theme.textSecondary}`}>
                {formData.propertyDescription || "Professionally staged to showcase the home with clarity, warmth, and a premium first impression."}
            </p>
        </div>
    );

    // Reusable photo component using container aspect ratios instead of fixed image heights
    const PhotoSlot = ({ url, aspect = "aspect-[4/3]", className = "", imageClassName = "", fill = false }) => (
        <div className={`relative overflow-hidden rounded-lg bg-slate-200 shadow-md ${fill ? "h-full min-h-0" : aspect} ${className}`}>
            {url ? (
                <img
                    src={url}
                    alt=""
                    crossOrigin="anonymous"
                    className={`absolute inset-0 h-full w-full object-cover ${imageClassName}`}
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-slate-400">
                    Photo
                </div>
            )}
        </div>
    );

    // Reusable property stats row
    const PropertyStats = ({ textColor, textMuted, size = "normal" }) => {
        const iconSize = size === "small" ? "w-4 h-4" : "w-5 h-5";
        const textSize = size === "small" ? "text-sm" : "text-lg";
        return (
            <div className={`flex items-center gap-${size === "small" ? "4" : "6"} flex-wrap`}>
                {formData.bedrooms && (
                    <div className="flex items-center gap-2">
                        <Bed className={iconSize} style={{ color: accentColor }} />
                        <span className={`${textSize} font-medium ${textColor}`}>{formData.bedrooms} Bed</span>
                    </div>
                )}
                {formData.bathrooms && (
                    <div className="flex items-center gap-2">
                        <Bath className={iconSize} style={{ color: accentColor }} />
                        <span className={`${textSize} font-medium ${textColor}`}>{formData.bathrooms} Bath</span>
                    </div>
                )}
                {formData.squareFeet && (
                    <div className="flex items-center gap-2">
                        <Square className={iconSize} style={{ color: accentColor }} />
                        <span className={`${textSize} font-medium ${textColor}`}>{formatDisplaySquareFeet(formData.squareFeet)}</span>
                    </div>
                )}
            </div>
        );
    };

    // Reusable agent branding block - ALWAYS shows agent info prominently
    // Uses resolvedAgentPhoto for PDF/image export compatibility
    const agentPhotoUrl = resolvedAgentPhoto || formData.agentPhoto;
    const brokerageLogoUrl = resolvedBrokerageLogo || formData.brokerageLogo;

    const AgentBlock = ({ textColor, textMuted, showPhoto = true, photoSize = "normal", layout = "horizontal" }) => {
        const photoClass = photoSize === "small" ? "w-24 h-28" : photoSize === "large" ? "w-40 h-48" : "w-32 h-40";

        if (layout === "vertical") {
            return (
                <div className="flex flex-col items-center text-center">
                    {showPhoto && (
                        agentPhotoUrl ? (
                            <img src={agentPhotoUrl} alt="" crossOrigin="anonymous" className={`${photoClass} object-cover object-top rounded-xl shadow-lg mb-3`} />
                        ) : (
                            <div className={`${photoClass} bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center mb-3`}>
                                <span className="text-4xl text-slate-400">{formData.agentName?.charAt(0) || "?"}</span>
                            </div>
                        )
                    )}
                    <div className={`text-base font-bold ${textColor}`}>{formData.agentName || "Agent Name"}</div>
                    {formData.agentTitle && <div className={`text-sm ${textMuted}`}>{formData.agentTitle}</div>}
                    {formData.brokerageName && <div className={`text-sm font-medium ${textMuted}`}>{formData.brokerageName}</div>}
                    <div className="mt-2 space-y-0.5">
                        {formData.agentPhone && <div className={`text-sm ${textColor}`}>{formData.agentPhone}</div>}
                        {formData.agentEmail && <div className={`text-sm ${textMuted}`}>{formData.agentEmail}</div>}
                    </div>
                </div>
            );
        }

        return (
            <div className="flex items-center gap-4">
                {showPhoto && (
                    agentPhotoUrl ? (
                        <img src={agentPhotoUrl} alt="" crossOrigin="anonymous" className={`${photoClass} object-cover object-top rounded-xl shadow-lg flex-shrink-0`} />
                    ) : (
                        <div className={`${photoClass} bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <span className="text-3xl text-slate-400">{formData.agentName?.charAt(0) || "?"}</span>
                        </div>
                    )
                )}
                <div>
                    <div className={`text-base font-bold ${textColor}`}>{formData.agentName || "Agent Name"}</div>
                    {formData.agentTitle && <div className={`text-xs ${textMuted}`}>{formData.agentTitle}</div>}
                    {formData.brokerageName && <div className={`text-sm font-medium ${textMuted}`}>{formData.brokerageName}</div>}
                    <div className="mt-1.5 space-y-0.5">
                        {formData.agentPhone && <div className={`text-sm ${textColor} flex items-center gap-1.5`}><Phone className="w-3.5 h-3.5" style={{ color: accentColor }} />{formData.agentPhone}</div>}
                        {formData.agentEmail && <div className={`text-sm ${textMuted} flex items-center gap-1.5`}><Mail className="w-3.5 h-3.5" style={{ color: accentColor }} />{formData.agentEmail}</div>}
                    </div>
                </div>
            </div>
        );
    };

    // Agent branding header bar - contact info at top
    const AgentHeaderBar = ({ textColor, isDark }) => (
        <div className={`flex items-center justify-between px-8 py-3 ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
            <div className="flex items-center gap-4">
                {agentPhotoUrl && (
                    <img src={agentPhotoUrl} alt="" crossOrigin="anonymous" className="w-10 h-10 rounded-full object-cover border-2" style={{ borderColor: accentColor }} />
                )}
                <div>
                    <span className={`text-sm font-bold ${textColor}`}>{formData.agentName || "Agent Name"}</span>
                    {formData.brokerageName && <span className={`text-sm ${textColor} opacity-60`}> · {formData.brokerageName}</span>}
                </div>
            </div>
            <div className={`flex items-center gap-6 text-sm ${textColor}`}>
                {formData.agentPhone && <span className="font-medium">{formData.agentPhone}</span>}
                {formData.agentEmail && <span>{formData.agentEmail}</span>}
            </div>
        </div>
    );

    // Agent branding footer bar
    const AgentFooterBar = ({ textColor, textMuted, isDark }) => (
        <div className={`flex items-center justify-between px-6 py-4 ${isDark ? "bg-white/5" : "bg-slate-50"} rounded-xl mx-4 mb-4`}>
            <div className="flex items-center gap-4">
                {agentPhotoUrl ? (
                    <img src={agentPhotoUrl} alt="" crossOrigin="anonymous" className="w-14 h-14 rounded-full object-cover border-2 shadow-md" style={{ borderColor: accentColor }} />
                ) : (
                    <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-xl text-slate-500">{formData.agentName?.charAt(0) || "?"}</span>
                    </div>
                )}
                <div>
                    <div className={`text-base font-bold ${textColor}`}>{formData.agentName || "Agent Name"}</div>
                    <div className={`text-sm ${textMuted}`}>{formData.agentTitle || "Real Estate Professional"}{formData.brokerageName ? ` · ${formData.brokerageName}` : ""}</div>
                </div>
            </div>
            <div className="text-right">
                {formData.agentPhone && <div className={`text-sm font-semibold ${textColor}`}>{formData.agentPhone}</div>}
                {formData.agentEmail && <div className={`text-sm ${textMuted}`}>{formData.agentEmail}</div>}
                {brokerageLogoUrl && <img src={brokerageLogoUrl} alt="" crossOrigin="anonymous" className="h-8 mt-2 ml-auto object-contain" />}
            </div>
        </div>
    );

    // Multiple flyer layout variations
    const FlyerBody = () => {
        const isDark = selectedTemplate.theme?.includes("noir") || selectedTemplate.theme?.includes("midnight") || selectedTemplate.theme?.includes("penthouse") || selectedTemplate.theme?.includes("platinum");
        const bgColor = isDark ? "bg-slate-950" : "bg-white";
        const textColor = isDark ? "text-white" : "text-slate-900";
        const textMuted = isDark ? "text-slate-400" : "text-slate-600";

        // Layout 1: Classic (like reference) - Hero top, 2 photos below, info + agent at bottom
        if (selectedTemplate.layout === "hero-top" || !selectedTemplate.layout) {
            return (
                <div className={`flex h-full flex-col ${bgColor}`}>
                    {/* Side accent bars */}
                    <div className="absolute left-0 top-0 bottom-0 w-2.5" style={{ backgroundColor: accentColor }} />
                    <div className="absolute right-0 top-0 bottom-0 w-2.5" style={{ backgroundColor: accentColor }} />

                    {/* Contact header */}
                    <div className={`flex items-center justify-center gap-8 py-3 px-10 ${textColor} text-sm font-medium`}>
                        {formData.agentEmail && <span>{formData.agentEmail}</span>}
                        {formData.agentPhone && <span>{formData.agentPhone}</span>}
                    </div>

                    {/* Main content */}
                    <div className="flex-1 px-10 pb-5">
                        <PhotoSlot url={heroPhoto} aspect="aspect-[16/9]" className="mb-3 shadow-lg" />
                        <div className="grid grid-cols-2 gap-3 mb-5">
                            <PhotoSlot url={secondaryPhoto} aspect="aspect-[4/3]" />
                            <PhotoSlot url={tertiaryPhoto} aspect="aspect-[4/3]" />
                        </div>

                        <div className="flex items-end justify-between gap-6">
                            <div className="flex-1">
                                <h1 className="text-4xl font-black tracking-tight mb-1" style={{ color: accentColor, fontFamily: theme.headingFont }}>{headline}</h1>
                                <h2 className={`text-lg font-semibold mb-3 ${textColor}`}>{formData.propertyAddress || "Property Address"}</h2>
                                <PropertyStats textColor={textColor} textMuted={textMuted} />
                                {formData.listingPrice && <div className="mt-2 text-2xl font-bold" style={{ color: accentColor }}>{formatDisplayPrice(formData.listingPrice)}</div>}
                            </div>
                            <AgentBlock textColor={textColor} textMuted={textMuted} />
                        </div>
                    </div>
                </div>
            );
        }

        // Layout 2: Split - Hero left (60%), info + photos stacked right
        if (selectedTemplate.layout === "split-editorial") {
            return (
                <div className={`flex h-full ${bgColor}`}>
                    {/* Left - Large Hero */}
                    <div className="w-[58%] p-5">
                        <PhotoSlot url={heroPhoto} fill className="shadow-xl rounded-xl" />
                    </div>

                    {/* Right - Info + Photos */}
                    <div className="w-[42%] p-5 pl-0 flex flex-col">
                        {/* Header */}
                        <div className="mb-4">
                            <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white mb-3" style={{ backgroundColor: accentColor }}>{selectedTemplate.badge}</div>
                            <h1 className="text-3xl font-black leading-tight mb-2" style={{ color: accentColor, fontFamily: theme.headingFont }}>{headline}</h1>
                            <h2 className={`text-sm font-medium ${textColor} flex items-center gap-1`}>
                                <MapPin className="w-4 h-4" style={{ color: accentColor }} />
                                {formData.propertyAddress || "Property Address"}
                            </h2>
                        </div>

                        {/* Stats */}
                        <div className={`p-3 rounded-xl mb-4 ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
                            <PropertyStats textColor={textColor} textMuted={textMuted} size="small" />
                            {formData.listingPrice && <div className="mt-2 text-xl font-bold" style={{ color: accentColor }}>{formatDisplayPrice(formData.listingPrice)}</div>}
                        </div>

                        {/* Two stacked photos */}
                        <div className="grid grid-cols-2 gap-2 mb-4">
                            <PhotoSlot url={secondaryPhoto} aspect="aspect-[4/3]" />
                            <PhotoSlot url={tertiaryPhoto} aspect="aspect-[4/3]" />
                        </div>

                        {/* Agent - bottom */}
                        <div className="mt-auto">
                            <AgentBlock textColor={textColor} textMuted={textMuted} photoSize="small" />
                        </div>
                    </div>
                </div>
            );
        }

        // Layout 3: Three Photo Strip - Hero top, 3 photos strip, info bottom
        if (selectedTemplate.layout === "event-focus") {
            return (
                <div className={`flex h-full flex-col ${bgColor}`}>
                    {/* Top bar with accent */}
                    <div className="h-2" style={{ backgroundColor: accentColor }} />

                    {/* Hero */}
                    <div className="px-6 pt-4">
                        <PhotoSlot url={heroPhoto} aspect="aspect-[16/9]" className="shadow-xl rounded-xl" />
                    </div>

                    {/* Three photo strip */}
                    <div className="grid grid-cols-3 gap-3 px-6 py-4">
                        <PhotoSlot url={secondaryPhoto} aspect="aspect-[4/3]" />
                        <PhotoSlot url={tertiaryPhoto} aspect="aspect-[4/3]" />
                        <PhotoSlot url={quaternaryPhoto} aspect="aspect-[4/3]" />
                    </div>

                    {/* Bottom info bar */}
                    <div className={`flex-1 px-6 pb-4 flex items-center justify-between gap-5 ${isDark ? "bg-white/5" : "bg-slate-50"} mx-6 rounded-xl`}>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-3xl font-black" style={{ color: accentColor, fontFamily: theme.headingFont }}>{headline}</h1>
                            <h2 className={`text-base font-semibold ${textColor} mb-2`}>{formData.propertyAddress}</h2>
                            <PropertyStats textColor={textColor} textMuted={textMuted} size="small" />
                        </div>
                        <div className="flex flex-col items-end text-right">
                            {formData.listingPrice && <div className="text-3xl font-bold mb-2" style={{ color: accentColor }}>{formatDisplayPrice(formData.listingPrice)}</div>}
                            <AgentBlock textColor={textColor} textMuted={textMuted} showPhoto photoSize="small" />
                            {brokerageLogoUrl && (
                                <img
                                    src={brokerageLogoUrl}
                                    alt=""
                                    crossOrigin="anonymous"
                                    className="mt-3 h-7 max-w-[120px] object-contain"
                                />
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        // Layout 4: Magazine - Full hero with overlay, photos strip at bottom
        if (selectedTemplate.layout === "minimal-poster") {
            return (
                <div className={`flex h-full flex-col ${bgColor}`}>
                    {/* Hero with text overlay */}
                    <div className="relative flex-1 m-4 mb-2 rounded-2xl overflow-hidden">
                        {heroPhoto ? (
                            <img src={heroPhoto} alt="" crossOrigin="anonymous" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-400" />
                        )}
                        {/* Dark gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        {/* Text overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                            <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2" style={{ backgroundColor: accentColor }}>{selectedTemplate.badge}</div>
                            <h1 className="text-4xl font-black mb-1" style={{ fontFamily: theme.headingFont }}>{headline}</h1>
                            <h2 className="text-lg font-medium opacity-90 mb-2">{formData.propertyAddress}</h2>
                            <div className="flex items-center gap-4 text-sm opacity-80">
                                {formData.bedrooms && <span>{formData.bedrooms} Bed</span>}
                                {formData.bathrooms && <span>{formData.bathrooms} Bath</span>}
                                {formData.squareFeet && <span>{formatDisplaySquareFeet(formData.squareFeet)}</span>}
                                {formData.listingPrice && <span className="font-bold text-lg" style={{ color: accentColor }}>{formatDisplayPrice(formData.listingPrice)}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Bottom: Photos + Agent */}
                    <div className="px-4 pb-4 flex items-center gap-3">
                        <div className="flex gap-2 flex-1">
                            <PhotoSlot url={secondaryPhoto} aspect="aspect-[4/3]" className="w-1/3" />
                            <PhotoSlot url={tertiaryPhoto} aspect="aspect-[4/3]" className="w-1/3" />
                            <PhotoSlot url={quaternaryPhoto} aspect="aspect-[4/3]" className="w-1/3" />
                        </div>
                        <div className="flex items-center gap-3">
                            {agentPhotoUrl && <img src={agentPhotoUrl} alt="" crossOrigin="anonymous" className="w-14 h-14 rounded-full object-cover object-top border-2" style={{ borderColor: accentColor }} />}
                            <div className="text-right">
                                <div className={`text-sm font-bold ${textColor}`}>{formData.agentName}</div>
                                {formData.agentTitle && <div className={`text-xs ${textMuted}`}>{formData.agentTitle}</div>}
                                {formData.brokerageName && <div className={`text-xs font-medium ${textMuted}`}>{formData.brokerageName}</div>}
                                {formData.agentPhone && <div className={`text-xs ${textColor} flex items-center gap-1 justify-end mt-0.5`}><Phone className="w-3 h-3" style={{ color: accentColor }} />{formData.agentPhone}</div>}
                                {formData.agentEmail && <div className={`text-xs ${textMuted} flex items-center gap-1 justify-end`}><Mail className="w-3 h-3" style={{ color: accentColor }} />{formData.agentEmail}</div>}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Layout 5: Mosaic Grid - Photos in mosaic, info panel
        if (selectedTemplate.layout === "mosaic-premium") {
            return (
                <div className={`flex h-full ${bgColor} p-4 gap-4`}>
                    {/* Left: Photo mosaic */}
                    <div className="w-[65%] grid grid-cols-2 grid-rows-2 gap-3">
                        <div className="col-span-2 row-span-1">
                            <PhotoSlot url={heroPhoto} aspect="aspect-[16/10]" className="rounded-xl shadow-lg" />
                        </div>
                        <PhotoSlot url={secondaryPhoto} aspect="aspect-[4/3]" className="rounded-xl" />
                        <PhotoSlot url={tertiaryPhoto} aspect="aspect-[4/3]" className="rounded-xl" />
                    </div>

                    {/* Right: Info panel */}
                    <div className={`w-[35%] rounded-2xl p-5 flex flex-col ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
                        <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white mb-3 self-start" style={{ backgroundColor: accentColor }}>{selectedTemplate.badge}</div>
                        <h1 className="text-2xl font-black leading-tight mb-2" style={{ color: accentColor, fontFamily: theme.headingFont }}>{headline}</h1>
                        <h2 className={`text-sm ${textColor} mb-4`}>{formData.propertyAddress}</h2>

                        <div className={`p-3 rounded-xl mb-4 ${isDark ? "bg-white/5" : "bg-white"}`}>
                            <PropertyStats textColor={textColor} textMuted={textMuted} size="small" />
                        </div>

                        {formData.listingPrice && <div className="text-2xl font-bold mb-4" style={{ color: accentColor }}>{formatDisplayPrice(formData.listingPrice)}</div>}

                        <div className="mt-auto">
                            <AgentBlock textColor={textColor} textMuted={textMuted} photoSize="small" />
                        </div>
                    </div>
                </div>
            );
        }

        // Layout 6: Bold Headline - Emphasis on typography
        if (selectedTemplate.layout === "feature-stack") {
            return (
                <div className={`flex h-full flex-col ${bgColor}`}>
                    {/* Giant headline section */}
                    <div className="px-8 pt-6 pb-4" style={{ backgroundColor: accentColor }}>
                        <h1 className="text-5xl font-black text-white tracking-tight mb-1" style={{ fontFamily: theme.headingFont }}>{headline}</h1>
                        <h2 className="text-xl text-white/90 font-medium">{formData.propertyAddress}</h2>
                    </div>

                    {/* Photos row */}
                    <div className="flex-1 flex gap-3 p-4">
                        <div className="w-1/2">
                            <PhotoSlot url={heroPhoto} fill className="rounded-xl shadow-lg" />
                        </div>
                        <div className="w-1/2 flex flex-col gap-3">
                            <PhotoSlot url={secondaryPhoto} fill className="rounded-xl flex-1" />
                            <PhotoSlot url={tertiaryPhoto} fill className="rounded-xl flex-1" />
                        </div>
                    </div>

                    {/* Bottom bar */}
                    <div className={`px-6 py-4 flex items-center justify-between ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
                        <div className="flex items-center gap-6">
                            <PropertyStats textColor={textColor} textMuted={textMuted} />
                            {formData.listingPrice && <div className="text-2xl font-bold" style={{ color: accentColor }}>{formatDisplayPrice(formData.listingPrice)}</div>}
                        </div>
                        <AgentBlock textColor={textColor} textMuted={textMuted} photoSize="small" />
                    </div>
                </div>
            );
        }

        // Layout 7: Dual Column - Two large photos side by side
        if (selectedTemplate.layout === "dual-column") {
            return (
                <div className={`flex h-full ${bgColor}`}>
                    {/* Two large photos */}
                    <div className="w-2/3 flex gap-3 p-4">
                        <div className="w-1/2">
                            <PhotoSlot url={heroPhoto} fill className="rounded-xl shadow-lg" />
                        </div>
                        <div className="w-1/2">
                            <PhotoSlot url={secondaryPhoto} fill className="rounded-xl shadow-lg" />
                        </div>
                    </div>

                    {/* Info panel */}
                    <div className="w-1/3 p-4 pl-0 flex flex-col">
                        <div className="h-1 rounded mb-4" style={{ backgroundColor: accentColor }} />
                        <h1 className="text-2xl font-black leading-tight mb-2" style={{ color: accentColor, fontFamily: theme.headingFont }}>{headline}</h1>
                        <h2 className={`text-sm ${textColor} mb-4 flex items-center gap-1`}>
                            <MapPin className="w-4 h-4" style={{ color: accentColor }} />
                            {formData.propertyAddress}
                        </h2>

                        <div className={`p-3 rounded-xl mb-3 ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
                            <PropertyStats textColor={textColor} textMuted={textMuted} size="small" />
                        </div>

                        {formData.listingPrice && <div className="text-2xl font-bold mb-4" style={{ color: accentColor }}>{formatDisplayPrice(formData.listingPrice)}</div>}

                        <div className="grid grid-cols-2 gap-2 mb-4">
                            <PhotoSlot url={tertiaryPhoto} aspect="aspect-[4/3]" />
                            <PhotoSlot url={quaternaryPhoto} aspect="aspect-[4/3]" />
                        </div>

                        <div className="mt-auto">
                            <AgentBlock textColor={textColor} textMuted={textMuted} photoSize="small" />
                        </div>
                    </div>
                </div>
            );
        }

        // Default fallback - same as hero-top
        return (
            <div className={`flex h-full flex-col ${bgColor}`}>
                <div className="absolute left-0 top-0 bottom-0 w-2.5" style={{ backgroundColor: accentColor }} />
                <div className="absolute right-0 top-0 bottom-0 w-2.5" style={{ backgroundColor: accentColor }} />
                <div className={`flex items-center justify-center gap-8 py-3 px-10 ${textColor} text-sm font-medium`}>
                    {formData.agentEmail && <span>{formData.agentEmail}</span>}
                    {formData.agentPhone && <span>{formData.agentPhone}</span>}
                </div>
                <div className="flex-1 px-10 pb-5">
                    <PhotoSlot url={heroPhoto} aspect="aspect-[16/9]" className="mb-3 shadow-lg" />
                    <div className="grid grid-cols-2 gap-3 mb-5">
                        <PhotoSlot url={secondaryPhoto} aspect="aspect-[4/3]" />
                        <PhotoSlot url={tertiaryPhoto} aspect="aspect-[4/3]" />
                    </div>
                    <div className="flex items-end justify-between gap-6">
                        <div className="flex-1">
                            <h1 className="text-4xl font-black tracking-tight mb-1" style={{ color: accentColor, fontFamily: theme.headingFont }}>{headline}</h1>
                            <h2 className={`text-lg font-semibold mb-3 ${textColor}`}>{formData.propertyAddress}</h2>
                            <PropertyStats textColor={textColor} textMuted={textMuted} />
                            {formData.listingPrice && <div className="mt-2 text-2xl font-bold" style={{ color: accentColor }}>{formatDisplayPrice(formData.listingPrice)}</div>}
                        </div>
                        <AgentBlock textColor={textColor} textMuted={textMuted} />
                    </div>
                </div>
            </div>
        );
    };

    if (!isOpen) {
        return null;
    }

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.96, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.96, opacity: 0 }}
                    className="flex h-[92vh] w-full max-w-[1500px] overflow-hidden rounded-[28px] bg-white shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex w-[380px] flex-shrink-0 flex-col border-r border-slate-200 bg-slate-50">
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900">Flyer Builder</h2>
                                <p className="mt-1 text-sm text-slate-500">Choose a template and auto-fit your staged listing photos.</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-200"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-6 py-5">
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Templates</label>
                                    <div className="grid grid-cols-1 gap-3">
                                        {PROPERTY_FLYER_TEMPLATES.map((template) => (
                                            <button
                                                key={template.id}
                                                onClick={() => setSelectedTemplateId(template.id)}
                                                className={`rounded-2xl border p-4 text-left transition-all ${
                                                    selectedTemplateId === template.id
                                                        ? "border-violet-500 bg-violet-50 ring-2 ring-violet-500/20"
                                                        : "border-slate-200 bg-white hover:border-violet-300"
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <div className="text-sm font-semibold text-slate-900">{template.name}</div>
                                                        <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">{template.useCase}</div>
                                                        <p className="mt-2 text-sm text-slate-600">{template.desc}</p>
                                                    </div>
                                                    {selectedTemplateId === template.id && (
                                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-500 text-white">
                                                            <Check className="h-4 w-4" />
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Headline Override</label>
                                    <input
                                        type="text"
                                        value={formData.customHeadline}
                                        onChange={(e) => updateField("customHeadline", e.target.value)}
                                        placeholder="Optional flyer headline"
                                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                    />
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                    <div className="mb-3">
                                        <label className="block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Property Details</label>
                                        <p className="mt-1 text-xs text-slate-400">Auto-populated from property info</p>
                                    </div>
                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            value={formData.propertyName}
                                            onChange={(e) => updateField("propertyName", e.target.value)}
                                            placeholder="Property name"
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                        />
                                        <input
                                            type="text"
                                            value={formData.propertyAddress}
                                            onChange={(e) => updateField("propertyAddress", e.target.value)}
                                            placeholder="Property address"
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                        />
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                value={formData.listingPrice}
                                                onChange={(e) => updateField("listingPrice", e.target.value)}
                                                placeholder="Price"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={formData.squareFeet}
                                                onChange={(e) => updateField("squareFeet", e.target.value)}
                                                placeholder="Square feet"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                value={formData.bedrooms}
                                                onChange={(e) => updateField("bedrooms", e.target.value)}
                                                placeholder="Bedrooms"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                            <input
                                                type="text"
                                                value={formData.bathrooms}
                                                onChange={(e) => updateField("bathrooms", e.target.value)}
                                                placeholder="Bathrooms"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                        </div>
                                        <textarea
                                            value={formData.propertyDescription}
                                            onChange={(e) => updateField("propertyDescription", e.target.value)}
                                            placeholder="Property description"
                                            rows={4}
                                            className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                    <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Accent Color</label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                value={accentColor}
                                                onChange={(e) => updateField("accentColor", e.target.value)}
                                                className="h-10 w-14 rounded-lg border border-slate-200 bg-white p-1 cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={accentColor}
                                                onChange={(e) => updateField("accentColor", e.target.value)}
                                                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                    <div className="mb-3 flex items-center justify-between gap-3">
                                        <label className="block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Flyer Photos</label>
                                        <span className="text-xs text-slate-400">Top 4 drive the layout</span>
                                    </div>
                                    <div className="space-y-3">
                                        {flyerPhotos.map((photo, index) => (
                                            <div
                                                key={photo.id}
                                                className={`rounded-2xl border p-3 ${
                                                    index < 4 ? "border-violet-200 bg-violet-50/50" : "border-slate-200 bg-slate-50"
                                                }`}
                                            >
                                                <div className="flex gap-3">
                                                    <img
                                                        src={getResolvedPhotoUrl(photo)}
                                                        alt={photo.room}
                                                        crossOrigin="anonymous"
                                                        className="h-20 w-24 rounded-xl object-cover"
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <p className="truncate text-sm font-semibold text-slate-900">{photo.room}</p>
                                                                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">
                                                                    {index === 0 ? "Hero" : index === 1 ? "Secondary" : index === 2 ? "Detail Three" : index === 3 ? "Detail Four" : `Position ${index + 1}`}
                                                                </p>
                                                            </div>
                                                            {index === 0 && (
                                                                <span className="rounded-full bg-violet-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                                                                    Cover
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setHeroFlyerPhoto(photo.id)}
                                                                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:border-violet-300 hover:text-violet-700"
                                                            >
                                                                Use as cover
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => moveFlyerPhoto(index, index - 1)}
                                                                disabled={index === 0}
                                                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:border-violet-300 hover:text-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                            >
                                                                <ChevronLeft className="h-3.5 w-3.5" />
                                                                Earlier
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => moveFlyerPhoto(index, index + 1)}
                                                                disabled={index === flyerPhotos.length - 1}
                                                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:border-violet-300 hover:text-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                            >
                                                                Later
                                                                <ChevronRight className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-slate-200 px-6 py-4 space-y-2">
                            <button
                                onClick={handleDownloadImage}
                                disabled={isDownloading || isResolvingImages}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 px-4 py-3 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                            >
                                {isDownloading ? <Sparkles className="h-4 w-4 animate-pulse" /> : <ImageIcon className="h-4 w-4" />}
                                {isDownloading ? "Rendering 4K..." : isResolvingImages ? "Preparing..." : "Download HD Image"}
                            </button>
                            <p className="text-center text-xs text-slate-500">
                                High-res PNG export
                            </p>
                            <button
                                onClick={handleDownloadPDF}
                                disabled={isDownloading || isResolvingImages}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400 disabled:opacity-50"
                            >
                                <Download className="h-4 w-4" />
                                Download PDF (for Print)
                            </button>
                        </div>
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col bg-[#dfe6ee]">
                        <div className="border-b border-slate-200 bg-white px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-900">{selectedTemplate.name}</h3>
                                    <p className="text-sm text-slate-500">{selectedTemplate.useCase} flyer preview</p>
                                </div>
                                <div className="text-sm text-slate-500">{flyerPhotos.length} staged photos available</div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto p-8">
                            <div className="mx-auto w-full max-w-[1180px]">
                                <div
                                    ref={flyerRef}
                                    className="relative mx-auto aspect-[11/8.5] w-full overflow-hidden rounded-lg shadow-[0_35px_80px_rgba(15,23,42,0.18)]"
                                    style={{ fontFamily: theme.bodyFont }}
                                >
                                    <FlyerBody />
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

PropertyFlyerBuilderModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    property: PropTypes.object,
    initialBranding: PropTypes.shape({
        listingPrice: PropTypes.string,
        bedrooms: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        bathrooms: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        squareFeet: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        propertyDescription: PropTypes.string,
        agentName: PropTypes.string,
        agentTitle: PropTypes.string,
        agentPhone: PropTypes.string,
        agentEmail: PropTypes.string,
        brokerageName: PropTypes.string,
        accentColor: PropTypes.string
    })
};

export default PropertyFlyerBuilderModal;
